import { useEffect, useRef, useState } from "react"
import styled from "styled-components"

import { ChevronDownIcon } from "@minuthq/meatball-ui-icons"
import { IconButton, Skeleton, spacing } from "@minuthq/meatball-ui-react"

import {
  Message,
  MessageProps,
} from "src/components/GuestInbox/GuestChat/Message"

const SCROLL_THRESHOLD = 100

export function ChatPanel({
  messages,
  onLoadMore,
  isLoadingMore,
  renderInput,
  emptyContent,
}: {
  messages: MessageProps[]
  onLoadMore: () => void
  isLoadingMore: boolean
  renderInput: () => React.ReactNode
  emptyContent?: React.ReactNode
}) {
  const [hasScrolledMessages, setHasScrolledMessages] = useState(false)
  const [showDownButton, setShowDownButton] = useState(false)

  const messagesWrapperRef = useRef<HTMLDivElement>(null)
  const latestMessageTimestampRef = useRef<number | null>(null)
  const hasScrolledToTop = useRef(false)

  function scrollToBottom({ smooth = true }: { smooth?: boolean }) {
    messagesWrapperRef.current?.scrollTo({
      top: messagesWrapperRef.current.scrollHeight,
      behavior: smooth ? "smooth" : "auto",
    })

    setShowDownButton(false)
  }

  function handleMessageScroll(event: React.UIEvent<HTMLDivElement>) {
    const scrollTop = event.currentTarget.scrollTop
    const scrollHeight = event.currentTarget.scrollHeight
    const height = event.currentTarget.offsetHeight

    const scrollBottom = Math.floor(scrollHeight - (scrollTop + height))

    if (scrollBottom > SCROLL_THRESHOLD) {
      setHasScrolledMessages(true)
    } else {
      setHasScrolledMessages(false)
      setShowDownButton(false)
    }

    if (scrollTop < SCROLL_THRESHOLD && !isLoadingMore) {
      if (!hasScrolledToTop.current) {
        onLoadMore()
        hasScrolledToTop.current = true
      } else {
        hasScrolledToTop.current = false
      }
    }
  }

  useEffect(() => {
    const latestMessage = messages[messages.length - 1]

    if (!latestMessage) {
      return
    }

    if (latestMessageTimestampRef.current === null) {
      scrollToBottom({ smooth: false })
      latestMessageTimestampRef.current = new Date(
        latestMessage.sentAt
      ).getTime()
      return
    }

    const latestMessageTimestamp = latestMessageTimestampRef.current

    const hasAnyNewMessagesFromSelf = !!messages.filter(
      (message) =>
        new Date(message.sentAt).getTime() > latestMessageTimestamp &&
        message.sender === "self"
    ).length

    if (hasAnyNewMessagesFromSelf) {
      scrollToBottom({ smooth: true })

      latestMessageTimestampRef.current = new Date(
        latestMessage.sentAt
      ).getTime()
      return
    }

    const hasUnreadMessagesFromOther = !!messages.filter(
      (message) =>
        new Date(message.sentAt).getTime() > latestMessageTimestamp &&
        message.sender === "other"
    ).length

    if (hasUnreadMessagesFromOther && !hasScrolledMessages) {
      scrollToBottom({ smooth: true })
    }

    if (hasUnreadMessagesFromOther && hasScrolledMessages) {
      setShowDownButton(true)
    }

    latestMessageTimestampRef.current = new Date(latestMessage.sentAt).getTime()
  }, [messages, hasScrolledMessages])

  return (
    <Wrapper>
      <MessagesWrapper ref={messagesWrapperRef} onScroll={handleMessageScroll}>
        {isLoadingMore &&
          new Array(3).fill(0).map((_, index) => (
            <MessageWrapper
              key={index}
              position={index % 2 === 0 ? "left" : "right"}
            >
              <Skeleton width="30ch" height={70} variant="rect" />
            </MessageWrapper>
          ))}

        {messages.length > 0 ? (
          messages.map((message) => (
            <MessageWrapper
              key={message.id}
              position={message.sender === "other" ? "left" : "right"}
            >
              <Message
                id={message.id}
                sentAt={message.sentAt}
                content={message.content}
                source={message.source}
                sender={message.sender}
                badges={message.badges}
              />
            </MessageWrapper>
          ))
        ) : emptyContent ? (
          <EmptyWrapper>{emptyContent}</EmptyWrapper>
        ) : null}

        <FloatingDownButton $visible={showDownButton}>
          <IconButton
            Icon={ChevronDownIcon}
            onClick={() => scrollToBottom({ smooth: true })}
            size="small"
            variant="subtle"
            color="neutral"
          />
        </FloatingDownButton>
      </MessagesWrapper>
      <div>{renderInput()}</div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`

const MessagesWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${spacing.L};
  overflow: auto;
  padding: ${spacing.M};
  padding-bottom: ${spacing.L};
`

const MessageWrapper = styled.div<{ position: "left" | "right" }>`
  max-width: clamp(min(90%, 400px), 70%, 1200px);
  margin-left: ${({ position }) => (position === "left" ? "0" : "auto")};
  margin-right: ${({ position }) => (position === "right" ? "0" : "auto")};
`

const FloatingDownButton = styled.div<{ $visible: boolean }>`
  position: sticky;
  height: 0;
  width: 100%;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
`

const EmptyWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`
